/* eslint-disable camelcase */
import { OptionType, SORT_ORDER, TableSortOption } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionInt,
	Core_Label_IssuesSortFields,
	Core_Label_IssuesSortOrder,
	Core_Label_IssuesTextSearchFields
} from 'src/common/types/graphql.generated';

import { buildDashboardIssuesSortParams } from '../../helpers/issueSortVariableBuilder';
import { GetAllIssuesQueryVariables } from '../AllIssues.generated';

const allIssuesSortFieldsMap: Record<string, Core_Label_IssuesSortFields> = {
	issueType: Core_Label_IssuesSortFields.TypeName,
	severity: Core_Label_IssuesSortFields.SeverityWeight,
	severityName: Core_Label_IssuesSortFields.SeverityName,
	pageTitle: Core_Label_IssuesSortFields.PageTitle,
	pageUrl: Core_Label_IssuesSortFields.Url,
	foundBy: Core_Label_IssuesSortFields.ProductName,
	sessionOrTest: Core_Label_IssuesSortFields.SessionName,
	user: Core_Label_IssuesSortFields.UserName
};

const allIssuesSortOrderMap: Record<SORT_ORDER, Core_Label_IssuesSortOrder> = {
	[SORT_ORDER.ASC]: Core_Label_IssuesSortOrder.Ascending,
	[SORT_ORDER.DESC]: Core_Label_IssuesSortOrder.Descending
};

const DEFAULT_DAYS_FILTER = 30;

const buildDaysFilter = (dateRangeOptions?: OptionType[]): ConditionInt | undefined => {
	if (!dateRangeOptions || dateRangeOptions.length === 0) {
		return { value: [DEFAULT_DAYS_FILTER] };
	}

	const days = Number(dateRangeOptions[0].value);
	if (Number.isNaN(days)) {
		return { value: [DEFAULT_DAYS_FILTER] };
	}

	return { value: [days] };
};

export const buildAllIssuesVariables = (
	filters: IFilterOptions,
	page: number,
	sizePerPage: number,
	sort?: TableSortOption
): GetAllIssuesQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<GetAllIssuesQueryVariables>({
		sortFieldsMap: allIssuesSortFieldsMap,
		sortOrderMap: allIssuesSortOrderMap
	});

	variablesBuilder.addPaginationParams(page, sizePerPage);

	const { sort: effectiveSort, sortFields } = buildDashboardIssuesSortParams(sort, true);

	variablesBuilder.addSortParam(effectiveSort, sortFields);

	const daysFilter = buildDaysFilter(filters.dateRange);
	if (daysFilter) {
		variablesBuilder.addConditionParam('daysFilter', daysFilter.value[0]);
	}

	variablesBuilder
		.addFilterParam('severityFilter', filters.severities)
		.addFilterParam('issueTypeFilter', filters.types)
		.addFilterParam('environmentFilter', filters.environment, true);

	if (filters.unitId && filters.unitId.length > 0 && filters.unitId[0]?.value !== '') {
		variablesBuilder.addFilterParam('businessUnitFilter', filters.unitId);
	}

	const variables = variablesBuilder.getVariables();

	if (filters.searchValue) {
		const searchValue = filters.searchValue.toString().trim();
		if (searchValue) {
			variables.textSearch = {
				searchFields: [
					Core_Label_IssuesTextSearchFields.TypeName,
					Core_Label_IssuesTextSearchFields.Url,
					Core_Label_IssuesTextSearchFields.PageTitle,
					Core_Label_IssuesTextSearchFields.SessionName
				],
				searchValues: [searchValue],
				insensitive: true
			};
		}
	}

	return variables;
};
